import { Injectable } from '@angular/core';
import { LogService } from '../logger/log.service';
import { CartInteractionCoordinator } from './cart-interaction.contracts';

/** Reused no-op for swallowing settle results without allocating a closure per call. */
const NOOP = (): void => undefined;

/**
 * Orders local cart mutations against irreversible side-effects (kitchen/bill print, one-click
 * pay, checkout navigation, payment finalize). It guarantees a product added before a print is
 * committed to the live cart before the print snapshots it.
 *
 * SRP: this service does ONE thing — ordering. It never mutates the cart, prints, navigates, or
 * holds UI state. Those stay in CartService / printer-link / the pages.
 *
 * Mechanism (event-driven, NO timers — required for old Android 9 WebViews and to avoid timer
 * smell):
 *  - Producers wrap each async add with {@link track}; the entry self-removes on settle.
 *  - Consumers await {@link whenIdle} before they snapshot the cart. whenIdle waits ONLY for adds
 *    in flight at call time (happens-before snapshot) → no starvation under continuous tapping.
 *  - {@link reset} (boundaries) clears tracked work AND releases any pending gate (epoch escape),
 *    so a business boundary can never stay blocked. MAX_TRACKED self-heals a producer leak.
 *
 * No-deadlock without timers: tracked work is a bounded add pipeline (DB resolve + addEntry) that
 * always settles; the epoch escape frees gates on any boundary; the cap drains a leak. If a hard
 * wall-clock guarantee is ever required it can be added behind this same contract (OCP) without
 * touching call sites.
 *
 * Android 9 compatibility: built from ES2015 primitives only (Set, Promise.all/race/resolve,
 * Array.from). It deliberately avoids `Promise.allSettled` (ES2020 / Chrome 76), which the app
 * does not polyfill and old WebViews lack — see {@link settleAll}.
 */
@Injectable({ providedIn: 'root' })
export class CartInteractionCoordinatorService extends CartInteractionCoordinator {
  /** Promises of cart mutations currently in flight. Each removes itself on settle. */
  private readonly inFlight = new Set<Promise<unknown>>();
  /** Resolvers of gates currently awaiting; released on reset() so boundaries never block. */
  private readonly pendingGateReleases = new Set<() => void>();
  /** Soft cap: above it we assume a producer leak and self-heal via reset(). */
  private readonly MAX_TRACKED = 64;

  private readonly logger = this.logService.createLogger('CartInteractionCoordinator');

  constructor(private readonly logService: LogService) {
    super();
  }

  track<T>(work: Promise<T>): Promise<T> {
    // .then(onSettle, onSettle) instead of .finally so we never depend on the finally polyfill,
    // and so a rejected add still drains its entry. The original `work` is returned untouched, so
    // callers keep the real value/rejection.
    const tracked: Promise<unknown> = Promise.resolve(work).then(
      () => this.untrack(tracked),
      () => this.untrack(tracked)
    );
    this.inFlight.add(tracked);

    if (this.inFlight.size > this.MAX_TRACKED) {
      this.logger.info('in-flight adds exceeded cap; resetting', { size: this.inFlight.size });
      this.reset();
    }

    return work;
  }

  async whenIdle(): Promise<void> {
    if (this.inFlight.size === 0) {
      return; // fast path: zero waiting, zero behaviour change when nothing is pending
    }

    const drained = this.settleAll(Array.from(this.inFlight)); // happens-before snapshot

    let release: () => void;
    const escape = new Promise<void>((resolve) => {
      release = resolve;
    });
    this.pendingGateReleases.add(release);

    try {
      await Promise.race([drained, escape]);
    } finally {
      this.pendingGateReleases.delete(release); // no lingering resolver → zero leak
    }
  }

  reset(): void {
    this.inFlight.clear();
    this.pendingGateReleases.forEach((release) => release()); // epoch escape: free pending gates
    this.pendingGateReleases.clear();
  }

  /** Remove a settled (or evicted) entry. */
  private untrack(tracked: Promise<unknown>): void {
    this.inFlight.delete(tracked);
  }

  /**
   * `Promise.allSettled`-equivalent built from ES2015 primitives. Resolves once every promise has
   * settled, ignoring rejections. Avoids `Promise.allSettled` (ES2020) which old Android 9 WebViews
   * (~Chrome 66) lack and which the app does not polyfill.
   */
  private settleAll(work: Promise<unknown>[]): Promise<unknown> {
    return Promise.all(work.map((entry) => entry.then(NOOP, NOOP)));
  }
}
